import { yupResolver } from '@hookform/resolvers/yup'
import { Button, Grid, Stack, Step, StepLabel, Stepper } from '@mui/material'
import React, { useContext, useMemo, useState } from 'react'
import {
  FormProvider,
  useForm,
  useFormContext,
  useWatch
} from 'react-hook-form'
import { useLocation, useNavigate } from 'react-router-dom'
import { useBoolean } from 'react-use'

import agent from 'agent'
import PageSection from 'components/common/PageSection'
import {
  getAuthenticationFormValidationSchema,
  ObservabilityTool
} from 'components/status-checks/authentication-form-utils'
import { StatusCheckConnectionForm } from 'components/status-checks/common/StatusCheckConnectionForm'
import { ObservabilityToolAuthentication } from 'components/status-checks/form/authentication/ObservabilityToolAuthentication'
import { useGetStatusChecksUrls } from 'components/status-checks/hooks/use-get-status-checks-urls'
import {
  buildSaveStatusCheckRequest,
  getDefaultStatusCheckFormValues,
  getStatusCheckFormValidationSchema,
  StatusCheckFormValues
} from 'components/status-checks/status-checks-form-utils'
import {
  StatusCheckDetailsContext,
  StatusCheckDetailsProvider
} from 'context/status-checks/StatusCheckDetailsContext'
import { useStatusCheckExternalIntegrationsContext } from 'context/status-checks/StatusCheckExternalIntegrationsContext'
import { useStatusChecksContext } from 'context/status-checks/StatusChecksContext'
import useAuth from 'hooks/use-auth'
import toast from 'lib/toast'
import { UserDefinedService } from 'types/api/services/service'
import { ObservabilityToolType } from 'types/api/status-checks/status-check'
import { messageFromResponseError } from 'utils/strings'

const CreateStatusCheckFormCreateButton: React.FC<{
  selectedObservabilityTool: ObservabilityTool
}> = ({ selectedObservabilityTool }) => {
  const { formState } = useFormContext<StatusCheckFormValues>()

  const { isStatusCheckConnectionValidated, isStatusCheckEvaluationValidated } =
    useContext(StatusCheckDetailsContext)

  // Should only validate the Success Evaluation when the observability tool type is custom
  const isSuccessEvaluationValid =
    selectedObservabilityTool.type === ObservabilityToolType.CUSTOM
      ? isStatusCheckEvaluationValidated
      : true

  const isCreateStatusCheckButtonDisabled =
    !formState.isValid ||
    formState.isSubmitting ||
    !isSuccessEvaluationValid ||
    !isStatusCheckConnectionValidated

  return (
    <Button
      key="formSubmit"
      data-testid="create-status-check-save-button"
      disabled={isCreateStatusCheckButtonDisabled}
      type="submit"
      variant="contained"
    >
      Create Health Check
    </Button>
  )
}

export const CreateStatusCheckForm: React.FC<{
  service?: UserDefinedService
}> = ({ service }) => {
  const [activeStep, setActiveStep] = useState(0)
  const [isAuthenticationSaving, setIsAuthenticationSaving] = useBoolean(false)
  const statusChecksUrls = useGetStatusChecksUrls()

  const STEPS = ['Authenticate', 'Connect Monitor or Alert']

  const { currentTeamId } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const { referrer = statusChecksUrls.base() } = location.state ?? {}

  const { statusChecks: existingStatusChecks, refetch: reFetchStatusChecks } =
    useStatusChecksContext()

  const {
    getAvailableExternalIntegrations,
    hasErrorExternalIntegrations,
    isFetchingExternalIntegrations,
    externalIntegrationsError
  } = useStatusCheckExternalIntegrationsContext()

  const externalIntegrations = getAvailableExternalIntegrations(false)

  const statusCheckFormValidationSchema = getStatusCheckFormValidationSchema(
    externalIntegrations,
    existingStatusChecks
  ).concat(getAuthenticationFormValidationSchema(externalIntegrations))

  const defaultValues: StatusCheckFormValues = getDefaultStatusCheckFormValues()

  const form = useForm<StatusCheckFormValues>({
    mode: 'all',
    defaultValues,
    resolver: yupResolver(statusCheckFormValidationSchema)
  })

  const {
    clearErrors,
    control,
    formState,
    handleSubmit,
    setValue,
    trigger,
    watch
  } = form

  const handleNext = async () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1)
  }

  const handleBack = () => {
    // Prevents the form validation from screaming at the user too much
    clearErrors()

    setActiveStep((prevActiveStep) => prevActiveStep - 1)
  }

  const handleSaveStatusCheck = async (values) => {
    try {
      const request = await buildSaveStatusCheckRequest(values, currentTeamId)

      const { data: newStatusCheckId } = await agent.post(
        `status-checks?teamId=${currentTeamId}`,
        request
      )

      // If creating a status check from the Service Settings menu, the new status check should be added to the Service
      if (service) {
        await agent.patch(
          `/services/${service.serviceId}?teamId=${currentTeamId}`,
          {
            statusCheckIds: [...service.statusCheckIds, newStatusCheckId]
          }
        )
      }

      toast({
        type: 'success',
        text: 'Health Check saved successfully.',
        time: 2_000
      })

      // Refresh status checks list
      await reFetchStatusChecks()

      navigate(referrer)
    } catch (err) {
      const error = messageFromResponseError(
        err,
        'There was an issue saving this Health Check. Please try again later.'
      )
      toast({ type: 'error', text: error })
    }
  }

  // Isolates a sub-schema for validating the "Authenticate" step only
  const authenticateStepValidationSchema = statusCheckFormValidationSchema.omit(
    [
      'category',
      'healthyStatusCode',
      'monitorUrl',
      'pollingIntervalSeconds',
      'predicates',
      'requestTimeout',
      'statusCheckName'
    ]
  )

  const [selectedObservabilityTool] = useWatch({
    control,
    name: ['observabilityTool']
  })

  const isAuthenticationFormValid = useMemo(() => {
    return authenticateStepValidationSchema.isValidSync(watch())
  }, [authenticateStepValidationSchema, watch])

  return (
    <PageSection
      errorMessage={messageFromResponseError(
        externalIntegrationsError,
        'An error occurred while fetching external integrations'
      )}
      hasError={hasErrorExternalIntegrations}
      isLoading={isFetchingExternalIntegrations}
    >
      <Stack gap={3}>
        <Stepper activeStep={activeStep} sx={{ width: '50%' }}>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        <FormProvider {...form}>
          <StatusCheckDetailsProvider
            control={control}
            formState={formState}
            serviceName={service?.name}
            setValue={setValue}
            trigger={trigger}
          >
            <form onSubmit={handleSubmit(handleSaveStatusCheck)}>
              <Grid container>
                <Grid item lg={9} xs={12}>
                  <Stack gap={2}>
                    {activeStep === 0 ? (
                      <ObservabilityToolAuthentication
                        externalIntegrations={externalIntegrations}
                        handleNext={handleNext}
                        isAuthenticationFormValid={isAuthenticationFormValid}
                        isAuthenticationSaving={isAuthenticationSaving}
                        setIsAuthenticationSaving={setIsAuthenticationSaving}
                      />
                    ) : (
                      <StatusCheckConnectionForm />
                    )}
                    <Stack direction="row" gap={2} mt={3}>
                      {activeStep === 1 ? (
                        <>
                          <Button variant="contained" onClick={handleBack}>
                            Back
                          </Button>
                          <CreateStatusCheckFormCreateButton
                            selectedObservabilityTool={
                              selectedObservabilityTool
                            }
                          />
                        </>
                      ) : null}
                    </Stack>
                  </Stack>
                </Grid>
              </Grid>
            </form>
          </StatusCheckDetailsProvider>
        </FormProvider>
      </Stack>
    </PageSection>
  )
}
